import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadIcon from "@mui/icons-material/Upload";
import {
  Button,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  GridRowsProp,
  GridToolbarProps,
  ToolbarPropsOverrides,
} from "@mui/x-data-grid-pro";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AddRowDropdown from "../../../../../../../components/Table/components/AddRowDropdown/AddRowDropdown";
import DeleteLinesOrderModal from "../../../../../../orders/components/DeleteLinesOrderModal/DeleteLinesOrderModal";
import ResetOrderModal from "../../../../../../orders/Entry/components/EntryLines/components/EntryLinesToolbar/components/ResetOrderModal/ResetOrderModal";

interface ToolbarPropsOverrides1 {
  selectedRow: GridRowsProp;
  deleteButtonCallback?: () => void;
  resetButtonCallback: () => void;
  isResetButtonDisabled: boolean;
  isViewErrorsEnabled: boolean;
  onViewErrorsChange: (checked: boolean) => void;
  importLinesButtonCallback: () => void;
  isMaxOrderLinesReached: boolean;
  addRows: (count: number) => void;
  viewIssuesOnly?: boolean;
  changeViewIssuesOnly?: (value: boolean) => void;
}

const EntryLinesToolbar = ({
  selectedRow,
  deleteButtonCallback,
  resetButtonCallback,
  isResetButtonDisabled,
  isViewErrorsEnabled,
  onViewErrorsChange,
  importLinesButtonCallback,
  isMaxOrderLinesReached,
  addRows,
  viewIssuesOnly = false,
  changeViewIssuesOnly,
}: GridToolbarProps & ToolbarPropsOverrides & ToolbarPropsOverrides1) => {
  const theme = useTheme();
  const { t } = useTranslation(["d2d", "common"]);
  const [showResetModal, setShowResetModal] = useState<boolean>(false);
  const [showDeleteLinesModal, setShowDeleteLinesModal] =
    useState<boolean>(false);

  useEffect(() => {
    if (!isViewErrorsEnabled && changeViewIssuesOnly) {
      changeViewIssuesOnly(false);
    }
  }, [isViewErrorsEnabled, changeViewIssuesOnly]);

  const OrderLinesHeaderSx = {
    display: "flex",
    justifyContent: "end",
    alignItems: "end",
    paddingRight: theme.tokens.FuelSpacing8,
    paddingTop: theme.tokens.FuelSpacing2,
    paddingBottom: theme.tokens.FuelSpacing2,
    gap: theme.tokens.FuelSpacing2,
    borderBottom: `1px solid ${theme.tokens.FuelColorBorderLight}`,
  };

  const isDeleteButtonDisabled = (): boolean => {
    if (selectedRow.length === 0) return true;
    const firstEditedSelectedRow = selectedRow.find(
      (row: any) => row.partNumber || row.quantity || row.bin
    );
    return !firstEditedSelectedRow;
  };

  const getOnChange = (event: ChangeEvent, checked: boolean) => {
    if (changeViewIssuesOnly) {
      changeViewIssuesOnly(checked);
    }
    onViewErrorsChange(checked);
  };

  return (
    <>
      <ResetOrderModal
        open={showResetModal}
        closeModal={() => {
          setShowResetModal(false);
        }}
        primaryButtonClick={() => {
          resetButtonCallback();
          setShowResetModal(false);
        }}
      />

      <DeleteLinesOrderModal
        open={showDeleteLinesModal}
        closeModal={() => {
          setShowDeleteLinesModal(false);
        }}
        primaryButtonClick={() => {
          deleteButtonCallback?.();
          setShowDeleteLinesModal(false);
        }}
      />

      <div data-testid="order-lines-header" style={OrderLinesHeaderSx}>
        <FormControlLabel
          disabled={!isViewErrorsEnabled}
          control={
            <Switch
              id="view-issues-only"
              data-testid="view-issues-only"
              checked={viewIssuesOnly}
              onChange={getOnChange}
            />
          }
          label={t("d2d:myLocatorEntry.switch.viewErrors")}
          labelPlacement="start"
          sx={{
            marginRight: "0.15em",
          }}
        />
        <Divider
          data-testid="vertical-divider"
          orientation="vertical"
          variant="middle"
          flexItem
        />
        <Tooltip
          title={t("d2d:myLocatorEntry.orderGrid.lines.reachedLimit")}
          disableFocusListener={!isMaxOrderLinesReached}
          disableHoverListener={!isMaxOrderLinesReached}
          disableTouchListener={!isMaxOrderLinesReached}
        >
          <div>
            <AddRowDropdown
              onAddRows={addRows}
              disabled={isMaxOrderLinesReached}
              toolTipProps={{
                title: t("d2d:myLocatorEntry.orderGrid.lines.reachedLimit"),
                disableFocusListener: !isMaxOrderLinesReached,
                disableHoverListener: !isMaxOrderLinesReached,
                disableTouchListener: !isMaxOrderLinesReached,
              }}
            />
          </div>
        </Tooltip>
        <Tooltip
          title={t("d2d:myLocatorEntry.orderGrid.lines.reachedLimit")}
          disableFocusListener={!isMaxOrderLinesReached}
          disableHoverListener={!isMaxOrderLinesReached}
          disableTouchListener={!isMaxOrderLinesReached}
        >
          <Button
            variant="text"
            data-testid="import-lines"
            startIcon={<UploadIcon data-testid="import-lines-icon" />}
            onClick={() => importLinesButtonCallback()}
            disabled={isMaxOrderLinesReached}
          >
            {t("d2d:myLocatorEntry.button.uploadFile")}
          </Button>
        </Tooltip>
        {deleteButtonCallback && (
          <Button
            variant="text"
            data-testid="delete-button"
            disabled={isDeleteButtonDisabled()}
            startIcon={<DeleteIcon data-testid="delete-icon" />}
            onClick={() => {
              setShowDeleteLinesModal(true);
            }}
          >
            {t("d2d:myLocatorEntry.button.deleteRow")}
          </Button>
        )}
        <Button
          data-testid="reset-button"
          variant="text"
          sx={{
            boxShadow: "none !important",
            display: "none",
          }}
          startIcon={<RefreshIcon data-testid="reset-icon" />}
          disabled={isResetButtonDisabled}
          onClick={() => {
            setShowResetModal(true);
          }}
        >
          {t("d2d:myLocatorEntry.button.reset")}
        </Button>
      </div>
    </>
  );
};

export default EntryLinesToolbar;
